// pipe: the ONE searchable ERP picker (SSOT / DRY), following the shared-module pattern of
// erp-date-field / erp-page-header / erp-table. Every ERP menu whose source can outgrow a glance —
// the 43-template Reports catalogue, the customer/job ref params — renders through THIS, so the
// narrow-down box, the keyboard model and the "what you are NOT seeing" statements are defined once.
//
// Three things it owns, each of which a per-screen copy kept getting wrong:
//
//   1. MATCHING. Forgiving by design (erp-searchable-menu.ts): case-, accent- and punctuation-
//      insensitive, token-based, over the label + the dim second line + invisible keywords. So
//      "massbalance", "mass balance" and "Mass-Balance" all find the same report, and a job number
//      that lives in the row's data (never in its title) is still reachable.
//   2. KEYBOARD. The popup is a combobox: the text box keeps DOM focus the whole time and the
//      option cursor is `aria-activedescendant`. Type to filter, ↑/↓ to move, Enter to pick, Escape
//      to close. The rows are deliberately NOT Radix menu items — a Radix item owns roving focus
//      and printable-key typeahead, which fight a text box in the same popup. That choice makes the
//      keyboard OURS to provide on BOTH variants: with `searchable={false}` there is no text box, so
//      the LIST itself takes DOM focus and the same handler (Radix's arrow/typeahead handling walks
//      its own item collection, which our rows are absent from — leaving that variant operable by
//      mouse only, which is how the Site picker regressed).
//   3. HONESTY. A picked value NEVER disappears (it stays on the trigger and pinned in the list even
//      when the source it came from has been replaced by a search), the render cap is stated, a
//      server-capped source is stated, and "loading" / "searching" / "the fetch failed" are each a
//      different line from "nothing matched" — the no-silent-partial rule the reports/exports
//      contract holds everywhere else. An empty list is only ever called empty when the source
//      really did arrive and really is empty.
//
// It never fetches. A caller that can widen its source server-side passes `onQueryChange` and does
// the debouncing + merging itself (reports/report-ref-search.ts), which keeps this a pure view.
import { useAutoFocus } from '@affine/component/hooks';
import { Input } from '@affine/component/ui/input';
import { Menu, MenuTrigger } from '@affine/component/ui/menu';
import { useI18n } from '@affine/i18n';
import { DoneIcon } from '@blocksuite/icons/rc';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  matchNormalized,
  normalizeOptions,
  SEARCHABLE_MENU_LIMIT,
  type SearchableOption,
  searchMatchScore,
  searchTokens,
} from './erp-search-match';
import * as styles from './erp-searchable-menu.css';

export type { SearchableOption } from './erp-search-match';

export interface ErpSearchableMenuProps {
  /** The pickable options, in the order the server gave them. Memoize at the call site: the folded
   *  match haystack is recomputed whenever this array identity changes. */
  options: readonly SearchableOption[];
  /** The picked id; `''` = nothing picked. */
  value: string;
  /** Receives the picked id (`''` when the clear row is chosen). */
  onSelect: (id: string) => void;
  /** Accessible name of the trigger, and the stem of the search box's accessible name. */
  label: string;
  /** Trigger caption while nothing is picked. */
  placeholder: string;
  /** Search-box placeholder (localized by the caller — it names what is being searched). */
  searchPlaceholder: string;
  /** Renders a leading row that clears the selection (the ref params' "Any"). */
  clearLabel?: string;
  /** Drop the narrow-down box for a small, complete, server-provided list (the site picker). */
  searchable?: boolean;
  /** Server-side widening hook: the raw term on every keystroke, and `''` when the menu closes (so
   *  the caller restores its default source). The CALLER debounces + merges — see report-ref-search. */
  onQueryChange?: (query: string) => void;
  /** The source's OWN first fetch is still in flight — a DISTINCT state from "the source is empty".
   *  Without it a picker opened while its list is loading states there is nothing to choose from. */
  loading?: boolean;
  /** A widen is pending or in flight — a DISTINCT state from "nothing matched". */
  searching?: boolean;
  /** The last fetch for this source (its page OR a widen) FAILED, so what is on screen is not
   *  evidence of what exists — the picker must never turn a server error into "no such customer". */
  loadFailed?: boolean;
  /** The source itself was capped server-side: more rows match than were fetched. */
  sourceCapped?: boolean;
  /** How many matches render at once; the rest are counted, never silently dropped. */
  limit?: number;
  /** Trigger sizing — REQUIRED and fail-closed: `MenuTrigger` renders its label `width: 0; flex: 1`,
   *  so a trigger with no definite width collapses to a bare chevron. Pass the host app's shared
   *  sizing variant (in @affine/core: `menuTriggerVariants.field` inside a formField column,
   *  `.toolbar` inside a toolbar row — the erp-menu-trigger drift pin enforces exactly this). */
  triggerClassName: string;
  /** Test/automation id for the trigger. The search box takes `<testId>-search`, the status lines
   *  `<testId>-loading` / `-searching` / `-failed` / `-empty` / `-more` / `-capped`. */
  testId?: string;
}

/** What the keyboard walks: the optional clear row, the pinned current selection, then the matches. */
interface Row {
  kind: 'clear' | 'current' | 'match';
  id: string;
  label: string;
  description?: string;
}

export function ErpSearchableMenu({
  options,
  value,
  onSelect,
  label,
  placeholder,
  searchPlaceholder,
  clearLabel,
  searchable = true,
  onQueryChange,
  loading = false,
  searching = false,
  loadFailed = false,
  sourceCapped = false,
  limit = SEARCHABLE_MENU_LIMIT,
  triggerClassName,
  testId,
}: ErpSearchableMenuProps) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** Where the user moved the option cursor, or `null` while it still sits on `defaultActive`. */
  const [cursor, setCursor] = useState<number | null>(null);
  // The last option we saw for `value`. A server-side widen REPLACES the source, so without this the
  // picked customer would vanish from the list and the trigger would read "Any" while a filter is
  // still applied — a silently wrong report parameterization.
  const [pinned, setPinned] = useState<SearchableOption | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  // With no search box there is nothing else in the popup that can hold the caret, so the list is
  // the focus target (the same macrotask focus the search box uses, which is what lands it AFTER
  // Radix's own mount-focus of the popup rather than racing it). One ref feeds both this and the
  // scroll-into-view below — the RowInput pattern.
  const listFocusRef = useAutoFocus<HTMLDivElement>(open && !searchable);
  const setListEl = useCallback(
    (el: HTMLDivElement | null) => {
      listRef.current = el;
      listFocusRef.current = el;
    },
    [listFocusRef]
  );

  // Folded once per source change, and only while the popup is actually open — a params form can
  // hold several pickers over thousands of rows each, and a closed one must cost nothing. A
  // keystroke then costs only substring tests (the standing frontend perf rule).
  const normalized = useMemo(
    () => (open ? normalizeOptions(options) : []),
    [open, options]
  );
  const deferredQuery = useDeferredValue(query);
  const tokens = useMemo(
    () => (searchable ? searchTokens(deferredQuery) : []),
    [searchable, deferredQuery]
  );
  const matched = useMemo(
    () => matchNormalized(normalized, tokens),
    [normalized, tokens]
  );

  const nearIds = useMemo(
    () =>
      new Set(
        normalized
          .filter(n => searchMatchScore(n, tokens) === 100)
          .map(n => n.option.id)
      ),
    [normalized, tokens]
  );

  const resolved = useMemo(
    () => (value === '' ? null : (options.find(o => o.id === value) ?? null)),
    [options, value]
  );
  useEffect(() => {
    setPinned(
      prev => resolved ?? (value !== '' && prev?.id === value ? prev : null)
    );
  }, [resolved, value]);
  const selected = resolved ?? (pinned?.id === value ? pinned : null);

  const rows = useMemo(() => {
    const out: Row[] = [];
    if (clearLabel !== undefined) {
      out.push({ kind: 'clear', id: '', label: clearLabel });
    }
    const shown = matched.slice(0, limit);
    if (value !== '' && !shown.some(o => o.id === value)) {
      // Pinned so the current selection is always visible AND re-pickable, whatever the query did to
      // the source. An id we can no longer resolve is labelled as such — never mislabelled "Any".
      out.push({
        kind: 'current',
        id: value,
        label:
          selected?.label ??
          t['com.affine.pipe.searchable-menu.unresolved']({
            id: value.slice(0, 8),
          }),
        description: selected?.description,
      });
    }
    for (const o of shown) {
      out.push({
        kind: 'match',
        id: o.id,
        label: o.label,
        description: nearIds.has(o.id)
          ? [t['com.affine.pipe.search.near'](), o.description]
              .filter(Boolean)
              .join(' · ')
          : o.description,
      });
    }
    return out;
  }, [clearLabel, matched, limit, value, selected, t, nearIds]);

  // Where the cursor sits before any arrow key: on the first MATCH while narrowing (so Enter picks
  // what was typed, not the clear row or the pinned selection above it), otherwise on the first row.
  const defaultActive = useMemo(() => {
    if (tokens.length === 0) return 0;
    const first = rows.findIndex(r => r.kind === 'match');
    return first >= 0 ? first : 0;
  }, [rows, tokens.length]);
  // The cursor is the DEFAULT until the user moves it (`null` = "wherever the default is"), and
  // every new term drops it back to null. Seeding an absolute index off `defaultActive` instead
  // would silently keep a stale cursor across a query change whenever the computed index happened
  // to be unchanged (0 → 0 with no clear row, 1 → 1 with one) — the match set moves under a cursor
  // a hover or an arrow key left behind, and Enter picks a row the user never aimed at.
  const activeIndex = Math.min(
    Math.max(cursor ?? defaultActive, 0),
    Math.max(rows.length - 1, 0)
  );

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      '[data-active="true"]'
    );
    // happy-dom has no layout engine and no scrollIntoView — the guard keeps the spec honest.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  const changeOpen = useCallback(
    (next: boolean) => {
      setOpen(next);
      // Every open starts from the top of the list, never wherever the last one was left.
      setCursor(null);
      if (!next) {
        // Closing drops the term AND tells the caller to restore its default source, so the next
        // open never starts against a source narrowed by a query that is no longer on screen.
        setQuery('');
        onQueryChange?.('');
      }
    },
    [onQueryChange]
  );

  const pick = useCallback(
    (row: Row) => {
      // PIN BEFORE CLOSING. Closing tells the caller to restore its default source (`onQueryChange('')`),
      // which React commits in the SAME batch as the new `value` — so a row that existed only in a
      // server-widened source is already gone from `options` by the first render that carries the
      // selection, `resolved` is null, and the pin effect has nothing to keep. The trigger would then
      // read "Selected (3f2a91cc)" for a customer the user picked BY NAME. Capturing the row the user
      // actually clicked is the only moment it is knowable.
      if (row.kind === 'match') {
        setPinned({
          id: row.id,
          label: row.label,
          description: row.description,
        });
      }
      onSelect(row.id);
      changeOpen(false);
    },
    [onSelect, changeOpen]
  );

  const onQueryInput = useCallback(
    (next: string) => {
      setQuery(next);
      // A new term is a new match set — re-seat the cursor on its default (see `activeIndex`).
      setCursor(null);
      onQueryChange?.(next);
    },
    [onQueryChange]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      // An IME composition commits with Enter, and the shared RowInput passes `onKeyDown` through
      // BEFORE its own composition guard — so without this, confirming a candidate while typing a
      // name picks whatever the partial term happened to highlight and closes the popup.
      if (e.nativeEvent.isComposing) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (rows.length === 0) return;
        setCursor(
          e.key === 'ArrowDown'
            ? Math.min(activeIndex + 1, rows.length - 1)
            : Math.max(activeIndex - 1, 0)
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const row = rows[activeIndex];
        if (row) pick(row);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        changeOpen(false);
      }
    },
    [rows, activeIndex, pick, changeOpen]
  );

  const triggerLabel =
    value === ''
      ? placeholder
      : (selected?.label ??
        t['com.affine.pipe.searchable-menu.unresolved']({
          id: value.slice(0, 8),
        }));

  const hidden = matched.length - Math.min(matched.length, limit);
  // Built only while open — a closed picker renders nothing but its trigger (see `normalized`).
  const body = !open ? null : (
    <div className={styles.panel}>
      {searchable && (
        <Input
          className={styles.searchInput}
          // The popup opens with the caret already in here. `autoFocus` focuses on a MACROTASK
          // (the shared hook), which is what makes it land AFTER Radix's own mount-focus of the
          // popup instead of racing it — the member-selector precedent. Radix's public content
          // props deliberately hide `onOpenAutoFocus`, so this is the supported way in.
          autoFocus
          value={query}
          onChange={onQueryInput}
          onKeyDown={onKeyDown}
          placeholder={searchPlaceholder}
          aria-label={`${label} — ${searchPlaceholder}`}
          role="combobox"
          aria-expanded
          aria-autocomplete="list"
          aria-controls={listId}
          aria-activedescendant={
            rows.length > 0 ? `${listId}-${activeIndex}` : undefined
          }
          data-testid={testId ? `${testId}-search` : undefined}
        />
      )}
      <div
        ref={setListEl}
        id={listId}
        role="listbox"
        aria-label={label}
        className={styles.list}
        // Without a search box the list itself is the combobox's keyboard home: it takes focus and
        // the SAME handler, so ↑/↓/Enter/Escape work identically on both variants. (With the search
        // box present the caret stays there and the list must not be a tab stop.)
        tabIndex={searchable ? undefined : 0}
        onKeyDown={searchable ? undefined : onKeyDown}
        aria-activedescendant={
          !searchable && rows.length > 0
            ? `${listId}-${activeIndex}`
            : undefined
        }
      >
        {rows.map((row, i) => (
          <div
            key={`${row.kind}:${row.id}`}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={row.id === value}
            data-active={i === activeIndex}
            className={styles.option}
            onMouseEnter={() => setCursor(i)}
            onClick={() => pick(row)}
          >
            <span className={styles.optionText}>
              <span className={styles.optionLabel}>{row.label}</span>
              {row.description && (
                <span className={styles.optionDescription}>
                  {row.description}
                </span>
              )}
            </span>
            {row.id === value && (
              <span className={styles.optionCheck}>
                <DoneIcon />
              </span>
            )}
          </div>
        ))}
      </div>
      {loading && (
        <div
          className={styles.hint}
          role="status"
          data-testid={testId ? `${testId}-loading` : undefined}
        >
          {t['com.affine.pipe.searchable-menu.loading']()}
        </div>
      )}
      {searching && !loading && (
        <div
          className={styles.hint}
          role="status"
          data-testid={testId ? `${testId}-searching` : undefined}
        >
          {t['com.affine.pipe.searchable-menu.searching']()}
        </div>
      )}
      {loadFailed && !loading && !searching && (
        <div
          className={styles.hint}
          role="status"
          data-testid={testId ? `${testId}-failed` : undefined}
        >
          {t['com.affine.pipe.searchable-menu.load-failed']()}
        </div>
      )}
      {!loading && !searching && !loadFailed && matched.length === 0 && (
        <div
          className={styles.hint}
          role="status"
          data-testid={testId ? `${testId}-empty` : undefined}
        >
          {/* Name what was searched — "nothing matched" is only actionable if it says for what.
              With no term at all the source itself is empty, which is a different sentence. Both
              are CLAIMS about the source, so they are made only once it is known: a list still
              loading, or one whose fetch failed, says so above instead. */}
          {query.trim() === ''
            ? t['com.affine.pipe.searchable-menu.empty-source']()
            : t['com.affine.pipe.searchable-menu.no-matches']({
                query: query.trim(),
              })}
        </div>
      )}
      {hidden > 0 && (
        <div
          className={styles.hint}
          role="status"
          data-testid={testId ? `${testId}-more` : undefined}
        >
          {/* `count` (a NUMBER, not a display string) is what i18next keys plural selection on —
              the `_one`/`_other` forms of this key are why it never reads "950 more match". */}
          {t['com.affine.pipe.searchable-menu.more-matches']({
            count: hidden,
          })}
        </div>
      )}
      {sourceCapped && !searching && (
        <div
          className={styles.hint}
          role="status"
          data-testid={testId ? `${testId}-capped` : undefined}
        >
          {t['com.affine.pipe.searchable-menu.source-capped']()}
        </div>
      )}
    </div>
  );

  return (
    <Menu
      rootOptions={{ open, onOpenChange: changeOpen }}
      contentOptions={{ className: styles.menuContent }}
      items={body}
    >
      <MenuTrigger
        className={triggerClassName}
        aria-label={label}
        data-testid={testId}
      >
        {triggerLabel}
      </MenuTrigger>
    </Menu>
  );
}
